import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import './AuthorDetail.css';
import { getWriterById, getItemListByUserId, getItemList } from "../../api";
import config from '../../config';
import { itemInvolvesWriterId } from "../../utils/authorContributors";
import { useLanguage } from '../../i18n/LanguageContext';
import { getLocalizedItemLabels, itemHasLocaleRow } from '../../utils/itemLocaleDisplay';
import { mergeLangArrays, fetchItemLangMapsByIds } from '../../utils/itemLangRows';

const ITEM_LANG_FETCH_CONCURRENCY = 12;

/**
 * Maddeler listesiyle aynı kaynak: tam katalogdaki `lang[]` + gerekirse itemLang tamamlama.
 */
async function enrichArticlesLangForLocale(articleRows, allCatalog, locale) {
  if (locale === 'tr' || !Array.isArray(allCatalog) || allCatalog.length === 0) {
    return articleRows;
  }

  const langById = new Map();
  const metaById = new Map();
  for (const row of allCatalog) {
    if (row?.id == null) continue;
    metaById.set(row.id, row.isMultiLang);
    if (!Array.isArray(row.lang) || row.lang.length === 0) continue;
    const prev = langById.get(row.id);
    langById.set(row.id, prev?.length ? mergeLangArrays(prev, row.lang) : row.lang);
  }

  let merged = articleRows.map((a) => {
    const catalogLang = langById.get(a.id);
    const catalogMeta = metaById.get(a.id);
    let next = { ...a };
    if (catalogMeta !== undefined) next = { ...next, isMultiLang: catalogMeta };
    if (catalogLang?.length) {
      next = {
        ...next,
        lang: mergeLangArrays(Array.isArray(a.lang) ? a.lang : [], catalogLang),
      };
    }
    return next;
  });

  const idsNeedingItemLang = merged
    .filter((row) => {
      if (itemHasLocaleRow(row, locale)) return false;
      const m = metaById.has(row.id) ? metaById.get(row.id) : row.isMultiLang;
      return m !== 0;
    })
    .map((r) => r.id);

  if (idsNeedingItemLang.length === 0) return merged;

  const extraMap = await fetchItemLangMapsByIds(idsNeedingItemLang, ITEM_LANG_FETCH_CONCURRENCY);
  return merged.map((row) => {
    const extra = extraMap.get(row.id);
    if (!extra?.length) return row;
    return {
      ...row,
      lang: mergeLangArrays(Array.isArray(row.lang) ? row.lang : [], extra),
    };
  });
}

const AuthorDetail = () => {
  const { id } = useParams();
  const { locale, t } = useLanguage();
  const [authorData, setAuthorData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [bioHasMore, setBioHasMore] = useState(false);
  const bioTextRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Yazar bilgilerini al
        const writerResponse = await getWriterById(id);
        if (writerResponse.status) {
          setAuthorData(writerResponse.data);
        }

        let articleRows = [];
        let allCatalog = null;
        const articlesResponse = await getItemListByUserId(id);
        if (articlesResponse.status && Array.isArray(articlesResponse.data)) {
          articleRows = [...articlesResponse.data];
        }

        // Ortak yazar: byUserId bazen sadece birincil kaydı döndürür; userIdList ile eşleşen maddeleri ekle
        try {
          const allRes = await getItemList();
          if (allRes.status && Array.isArray(allRes.data)) {
            allCatalog = allRes.data;
            const seen = new Set(articleRows.map((a) => String(a.id)));
            for (const item of allRes.data) {
              if (!itemInvolvesWriterId(item, id)) continue;
              if (seen.has(String(item.id))) continue;
              seen.add(String(item.id));
              articleRows.push(item);
            }
          }
        } catch (e) {
          console.warn('Ortak yazar maddeleri birleştirilemedi:', e);
        }

        articleRows = await enrichArticlesLangForLocale(articleRows, allCatalog, locale);
        setArticles(articleRows);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, locale]);

  useLayoutEffect(() => {
    if (!authorData?.cv || loading) return;
    const el = bioTextRef.current;
    if (!el) return;
    if (bioExpanded) {
      setBioHasMore(true);
      return;
    }
    setBioHasMore(el.scrollHeight > el.clientHeight + 1);
  }, [authorData?.cv, loading, bioExpanded]);

  if (loading) {
    return (
      <div className="author-detail-loading" role="status" aria-live="polite">
        <Spinner animation="border" aria-hidden="true" />
        <p>{t('authorDetail.loading')}</p>
      </div>
    );
  }

  if (!authorData) {
    return <div className="author-detail-error">{t('authorDetail.notFound')}</div>;
  }

  const entriesSectionTitle = t('authorDetail.entriesHeading')
    .replace('{name}', authorData.name ?? '')
    .replace('{surname}', authorData.surname ?? '')
    .replace(/\s+/g, ' ')
    .trim();

  const createdDateLocale = locale === 'tr' ? 'tr-TR' : locale === 'fr' ? 'fr-FR' : 'en-GB';

  return (
    <div className="author-detail-container">
      <div className="author-detail-header">
        <div className="author-detail-info">
          <img 
            src={
              authorData.profileImageUrl
                ? config.userAuthApiUrl + authorData.profileImageUrl
                : config.defaultProfileImageUrl
            }
            alt={t('authorDetail.profileAlt')}
            className="author-detail-profile-image"
           
          />
          <h1 className="author-detail-name">
          {authorData.title &&authorData.title} {authorData.name} {authorData.surname}
          </h1>
         

 
        </div>
      </div>

      <div className="author-detail-content">
        {authorData.cv && (
          <div className="author-detail-biography">
            <h2 className="author-detail-section-title">{t('authorDetail.biography')}</h2>
            <div className="author-detail-biography-inner">
              <p
                ref={bioTextRef}
                className={`author-detail-text${!bioExpanded ? ' author-detail-text--collapsed' : ''}`}
              >
                {authorData.cv}
              </p>
              {bioHasMore && (
                <button
                  type="button"
                  className="author-detail-bio-toggle"
                  onClick={() => setBioExpanded((e) => !e)}
                  aria-expanded={bioExpanded}
                >
                  {bioExpanded ? t('authorDetail.readLess') : t('authorDetail.readMore')}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="author-detail-articles">
          <h2 className="author-detail-section-title">
            {entriesSectionTitle}{' '}
            <span className="article-count">({articles.length})</span>
          </h2>
          <div className="author-detail-articles-list">
            {articles.map((article) => {
              const labels = getLocalizedItemLabels(article, locale);
              return (
              <Link 
                to={`/article/${article.id}`}
                key={article.id} 
                className="author-detail-article-item"
              >
                <div className="article-header">
                  <h3 className="author-detail-article-title">{labels.title}</h3>
                  {article.createdAt && (
                    <span className="article-date">
                      {new Date(article.createdAt).toLocaleDateString(createdDateLocale, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                </div>
                {labels.definition && (
                  <p className="author-detail-article-summary">{labels.definition}</p>
                )}
              </Link>
            );
            })}
          </div>
        </div>

        {articles.length === 0 && (
          <div className="author-detail-no-articles">
            <p>{t('authorDetail.noArticles')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorDetail; 